import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  History, 
  Search, 
  Filter, 
  Download, 
  User, 
  Calendar,
  FileText,
  FolderOpen,
  Package,
  Users,
  Send,
  Mail,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Eye,
  Clock,
  Activity,
  BarChart3,
  ArrowRight,
  X
} from 'lucide-react';
import axios from 'axios';
import { format, formatDistanceToNow, parseISO, subDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Entity type icons
const entityIcons = {
  document: FileText,
  project: FolderOpen,
  asset: Package,
  user: Users,
  transmittal: Send,
  correspondence: Mail,
  role: Users,
  task: Activity,
  contact: User,
  document_type: FileText,
  revision_code: FileText,
  discipline: FileText,
  approval_code: FileText,
  status_code: FileText,
  template: FileText,
};

// EntityIcon component - moved outside to avoid recreation on each render
const EntityIcon = ({ type }) => {
  const Icon = entityIcons[type] || FileText;
  return <Icon className="w-4 h-4" />;
};

// Action colors
const actionColors = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  upload: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  approve: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  reject: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  submit_approval: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  login: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  import: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  export: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  request_changes: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

const AuditTrail = () => {
  const { token, hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('all');
  const [userId, setUserId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  
  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType !== 'all') params.append('entity_type', entityType);
      if (action !== 'all') params.append('action', action);
      if (userId !== 'all') params.append('user_id', userId);
      if (searchTerm) params.append('search', searchTerm);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await axios.get(`${API_URL}/api/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.audit_logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, entityType, action, userId, searchTerm, dateFrom, dateTo, offset]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  }, [token]);

  // Fetch available actions and entity types
  const fetchActions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableActions(response.data.actions);
      setEntityTypes(response.data.entity_types);
    } catch (error) {
      console.error('Failed to load actions', error);
    }
  }, [token]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load users', error);
    }
  }, [token]);

  useEffect(() => {
    if (hasPermission('admin')) {
      fetchLogs();
      fetchStats();
      fetchActions();
      fetchUsers();
    }
  }, [fetchLogs, fetchStats, fetchActions, fetchUsers, hasPermission]);

  // Export to CSV
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (entityType !== 'all') params.append('entity_type', entityType);
      if (action !== 'all') params.append('action', action);
      if (userId !== 'all') params.append('user_id', userId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('format', 'csv');

      const response = await axios.get(`${API_URL}/api/audit-logs/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit logs exported successfully');
    } catch (error) {
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setEntityType('all');
    setAction('all');
    setUserId('all');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setOffset(0);
  };

  // Quick date filters
  const setQuickDateFilter = (days) => {
    setDateFrom(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setDateTo(format(new Date(), 'yyyy-MM-dd'));
    setOffset(0);
  };

  if (!hasPermission('admin')) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="w-12 h-12 text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You need admin permissions to view the audit trail.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <History className="w-7 h-7" />
            Audit Trail
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system activities and changes
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchLogs(); fetchStats(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold">{stats.total_logs.toLocaleString()}</p>
                </div>
                <Activity className="w-8 h-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Entity Types</p>
                  <p className="text-2xl font-bold">{stats.entity_by_type.length}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-2xl font-bold">{stats.most_active_users.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Action Types</p>
                  <p className="text-2xl font-bold">{stats.actions_by_type.length}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowFilters(!showFilters)}>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Search & Filters
            </CardTitle>
            {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent className="space-y-4">
            {/* Quick date filters */}
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter(1)}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter(7)}>Last 7 days</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter(30)}>Last 30 days</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateFilter(90)}>Last 90 days</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, details..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setOffset(0); }}
                    className="pl-9"
                  />
                </div>
              </div>

              {/* Entity Type */}
              <div>
                <Label>Entity Type</Label>
                <Select value={entityType} onValueChange={(v) => { setEntityType(v); setOffset(0); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="document">Documents</SelectItem>
                    <SelectItem value="project">Projects</SelectItem>
                    <SelectItem value="asset">Assets</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                    <SelectItem value="transmittal">Transmittals</SelectItem>
                    <SelectItem value="correspondence">Correspondence</SelectItem>
                    <SelectItem value="role">Roles</SelectItem>
                    <SelectItem value="task">Tasks</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action */}
              <div>
                <Label>Action</Label>
                <Select value={action} onValueChange={(v) => { setAction(v); setOffset(0); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {availableActions.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* User */}
              <div>
                <Label>User</Label>
                <Select value={userId} onValueChange={(v) => { setUserId(v); setOffset(0); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.user_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reset */}
              <div className="flex items-end">
                <Button variant="outline" onClick={resetFilters} className="w-full">
                  <X className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
                />
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Activity Timeline
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              Showing {logs.length} of {total.toLocaleString()} events
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mb-4 opacity-50" />
              <p>No audit logs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />
              
              {/* Timeline items */}
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <div 
                    key={log.id} 
                    className="relative pl-14 pr-4 py-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => setSelectedLog(log)}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-4 top-4 w-5 h-5 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      <EntityIcon type={log.entity_type} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={actionColors[log.action] || 'bg-gray-100 text-gray-800'}>
                            {log.action_display || log.action}
                          </Badge>
                          <span className="font-medium capitalize">{log.entity_type}</span>
                          {log.entity_name && (
                            <>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <span className="text-primary font-medium truncate max-w-xs">
                                {log.entity_name}
                              </span>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {log.details || `${log.action_display} ${log.entity_type}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span>{log.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span title={format(parseISO(log.timestamp), 'PPpp')}>
                            {formatDistanceToNow(parseISO(log.timestamp), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Audit Log Details
            </DialogTitle>
            <DialogDescription>
              {selectedLog && format(parseISO(selectedLog.timestamp), 'PPpp')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Action Badge */}
              <div className="flex items-center gap-4">
                <Badge className={`${actionColors[selectedLog.action] || 'bg-gray-100'} text-lg px-4 py-2`}>
                  {selectedLog.action_display || selectedLog.action}
                </Badge>
                <span className="text-lg capitalize">{selectedLog.entity_type}</span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">User</Label>
                  <p className="font-medium">{selectedLog.user_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedLog.user_email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Entity</Label>
                  <p className="font-medium">{selectedLog.entity_name || 'N/A'}</p>
                  <p className="text-sm text-muted-foreground font-mono">{selectedLog.entity_id}</p>
                </div>
              </div>

              {/* Details */}
              {selectedLog.details && (
                <div>
                  <Label className="text-muted-foreground">Details</Label>
                  <p className="mt-1">{selectedLog.details}</p>
                </div>
              )}

              {/* Changes */}
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Changes</Label>
                  <div className="mt-2 border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Field</th>
                          <th className="text-left px-4 py-2 font-medium">Old Value</th>
                          <th className="text-left px-4 py-2 font-medium">New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedLog.changes).map(([field, values]) => (
                          <tr key={field} className="border-t">
                            <td className="px-4 py-2 font-medium capitalize">{field.replace(/_/g, ' ')}</td>
                            <td className="px-4 py-2 text-red-600 dark:text-red-400">
                              {typeof values.old === 'object' ? JSON.stringify(values.old) : String(values.old || '-')}
                            </td>
                            <td className="px-4 py-2 text-green-600 dark:text-green-400">
                              {typeof values.new === 'object' ? JSON.stringify(values.new) : String(values.new || '-')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div>
                <Label className="text-muted-foreground">Timestamp</Label>
                <p className="font-mono text-sm mt-1">{selectedLog.timestamp}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditTrail;
